<template>
  <div class="integration-detail-grid">
    <div class="integration-stack">
      <DetailCard title="Operation overview">
        <Metadata :fields="overviewFields" />
        <p v-if="detail.source && (detail.source.label || detail.source.object)" class="integration-detail-card__note">
          Source: {{ detail.source.label }}
          <template v-if="detail.source.object">
            ·
            <RelatedObject :object="detail.source.object" />
          </template>
        </p>
      </DetailCard>
      <DetailCard title="Status">
        <Metadata :fields="statusFields" />
      </DetailCard>
      <DetailCard title="Attempt summary">
        <Metadata :fields="attemptFields" />
      </DetailCard>
      <DetailCard v-if="hasFields(technicalFields)" title="Technical details">
        <Metadata :fields="technicalFields" />
      </DetailCard>
      <DetailCard v-if="canEdit" title="Recovery">
        <RecoveryControls :detail="detail" @changed="changed" @refresh="$emit('refresh')" @denied="$emit('denied')" />
      </DetailCard>
      <DetailCard v-if="detail.effects && detail.effects.length" title="Business updates">
        <article v-for="effect in detail.effects" :key="effect.id" class="integration-item">
          <Metadata
            :fields="{
              Update: effect.kind ? humanize(effect.kind) : null,
              Status: effect.status ? humanize(effect.status) : null,
              Attempts: effect.attempt_count,
              Applied: date(effect.applied_at, true),
              Error: effect.error,
            }"
          />
          <RelatedObject v-if="effect.target" :object="effect.target" />
        </article>
      </DetailCard>
      <DetailCard v-if="recipientItems.length > 1" title="Recipients">
        <p v-for="(recipient, index) in recipientItems" :key="index">
          {{ recipient.label || recipient.address }}
        </p>
      </DetailCard>
    </div>
    <div class="integration-stack">
      <DetailCard v-if="hasContent" title="Message content">
        <Metadata :fields="contentFields" />
        <template v-if="detail.content && detail.content.html">
          <h3>Saved HTML preview</h3>
          <p class="integration-muted">External images and interactive content are disabled.</p>
          <iframe
            class="integration-html"
            tabindex="-1"
            sandbox=""
            referrerpolicy="no-referrer"
            title="Saved message HTML preview"
            :srcdoc="html"
          />
        </template>
        <p v-for="(attachment, index) in attachments" :key="attachment.id || index">
          {{ attachment.name }}
          <template v-if="attachment.size_bytes != null">· {{ attachment.size_bytes }} bytes</template>
        </p>
        <PayloadViewer v-if="hasValue('reply_markup')" title="Reply markup" origin-label="Saved message content" :payload="snapshot(detail.content.reply_markup)" />
        <PayloadViewer v-if="hasValue('template_variables')" title="Template variables" origin-label="Saved message content" :payload="snapshot(detail.content.template_variables)" />
      </DetailCard>
      <DetailCard v-if="detail.knowledge_ai" title="Knowledge AI">
        <Metadata :fields="knowledgeFields" />
        <p v-if="detail.knowledge_ai.blocking_operation_id">
          Waiting for
          <router-link :to="{ name: 'tech-support-message-operations-knowledge_ai', query: { operation: detail.knowledge_ai.blocking_operation_id } }">
            {{ detail.knowledge_ai.blocking_operation_id }}
          </router-link>
        </p>
        <PayloadViewer title="Input messages" origin-label="Saved input messages" :payload="snapshot(detail.knowledge_ai.inputs)" />
        <PayloadViewer title="Prompt profile" origin-label="Saved profile" :payload="snapshot(detail.knowledge_ai.profile)" />
        <PayloadViewer v-if="detail.knowledge_ai.response" title="Response" origin-label="Knowledge AI response" :payload="snapshot(detail.knowledge_ai.response)" />
      </DetailCard>
      <Attempts :detail="detail" :refresh-key="detail.integration === 'knowledge_ai' ? detail.version : refreshKey" @denied="$emit('denied')" />
    </div>
  </div>
</template>

<script>
import Metadata from './Metadata'
import RelatedObject from './RelatedObject'
import PayloadViewer from './PayloadViewer'
import Attempts from './Attempts'
import RecoveryControls from './RecoveryControls'
import DetailCard from './DetailCard'
import { techSupportKey } from '@/utils/techSupportAccess'
import { dateLabel, humanize, recipientLabel, safeHtml, messageText, templateLabel } from './model'

export default {
  components: { Metadata, RelatedObject, PayloadViewer, Attempts, RecoveryControls, DetailCard },
  props: { detail: { type: Object, required: true } },
  data: () => ({ refreshKey: 0 }),
  computed: {
    canEdit() { return this.$store.getters['permissions/can'](techSupportKey('message_operations', 'edit')) },
    knowledgeFields() {
      const value = this.detail.knowledge_ai
      return value ? {
        'Session': value.session_id,
        'Profile': value.profile?.key,
        'Profile version': value.profile?.version,
        'Frozen payload': value.frozen ? 'Yes' : 'No',
        'Attempts in this cycle': value.cycle_attempts,
        'Dead-lettered': dateLabel(value.dead_lettered_at, true),
        'Answer': value.response?.text
      } : {}
    },
    html() { return safeHtml(this.detail.content?.html || '') },
    attachments() { return this.detail.content?.attachments || [] },
    recipientItems() { return this.detail.recipient?.items || [] },
    contentFields() {
      const content = this.detail.content || {}
      const template = this.detail.template || {}
      return {
        'Subject': this.detail.subject,
        'Text': messageText(content.text),
        'Caption': messageText(content.caption),
        'Photo reference': content.photo_reference,
        'Template': templateLabel(this.detail.template),
        'Language': template.language
      }
    },
    hasContent() {
      return Object.values(this.contentFields).some(value => value !== null && value !== undefined && value !== '' && value !== '—') ||
        Boolean(this.attachments.length || this.hasValue('reply_markup') || this.hasValue('template_variables') || this.detail.content?.html)
    },
    overviewFields() {
      const value = this.detail
      return {
        'Operation': value.operation_type ? humanize(value.operation_type) : null,
        'Created': dateLabel(value.created_at, true),
        'Recipient': recipientLabel(value.recipient),
        'Account': value.account?.label,
        'Sender': value.sender
      }
    },
    statusFields() {
      const value = this.detail
      return {
        'Execution': value.execution_status ? humanize(value.execution_status) : null,
        'Delivery': value.delivery_status ? humanize(value.delivery_status) : null,
        'Business updates': value.sync_status ? humanize(value.sync_status) : null,
        ...(value.external_ids || {})
      }
    },
    attemptFields() {
      const value = this.detail
      return {
        'Attempts': value.attempt_count,
        'Last attempt': dateLabel(value.last_attempt_at, true),
        'Last result': value.last_result?.message,
        'Result time': dateLabel(value.last_result?.at, true)
      }
    },
    technicalFields() {
      const value = this.detail
      return {
        'Next attempt': dateLabel(value.next_attempt_at, true),
        'Result code': value.last_result?.code,
        'HTTP status': value.last_result?.http_status,
        'Attention reasons': value.attention_reasons?.length ? value.attention_reasons.map(humanize).join('; ') : null
      }
    }
  },
  methods: {
    date: dateLabel,
    humanize,
    hasValue(key) {
      const value = this.detail.content?.[key]
      return value !== null && value !== undefined
    },
    hasFields(fields) {
      return Object.values(fields).some(value => value !== null && value !== undefined && value !== '' && value !== '—')
    },
    snapshot(value) { return { availability: 'available', format: 'json', value } },
    changed() { this.refreshKey++; this.$emit('changed') }
  }
}
</script>
